import React from 'react';
import { TextAreaField, SectionHeader } from './FormField';
import { Label } from '@/components/ui/label';
import { GUIDELINES, GUIDELINE_GROUPS } from '@/components/vla/guidelineCriteria';

export default function Step8LegalDetails({ data, onChange }) {
  // The PDF only prints vla_guideline, so the chosen guideline goes on its first line.
  const handleGuidelineChange = (code) => {
    const oldLabel = GUIDELINES[data.vla_guideline_code]?.label;
    const text = data.vla_guideline || "";
    const rest = oldLabel && text.startsWith(oldLabel) ? text.slice(oldLabel.length).replace(/^\n/, "") : text;
    const label = GUIDELINES[code]?.label;
    onChange("vla_guideline_code", code);
    onChange("vla_guideline", label ? (rest ? `${label}\n${rest}` : label) : rest);
    // Each guideline has its own criteria, so ticks for the old one don't carry over.
    if (code !== data.vla_guideline_code) onChange("guideline_criteria", {});
  };

  // The guideline certification, done here rather than on a separate form. Send for
  // certification builds the Guideline Certification document from these answers.
  const guideline = GUIDELINES[data.vla_guideline_code];
  const criteria = data.guideline_criteria || {};
  const setCriterion = (id, checked) => {
    const next = { ...criteria };
    if (checked) next[id] = true;
    else delete next[id];
    onChange("guideline_criteria", next);
  };

  return (
    <div className="space-y-8">
      <div>
        <SectionHeader number="20" title="Describe your legal problem" />
        <div className="p-4 bg-blue-50 rounded-lg mb-5 text-sm text-gray-600">
          For criminal matters, please provide details of the charges; for family matters and family violence matters, please specify the issue(s), the background, and the orders being sought (e.g. residence of children).
        </div>
        <TextAreaField label="Please provide details of your legal problem:" name="legal_problem_details" value={data.legal_problem_details} onChange={onChange} rows={6} />
      </div>

      <div>
        <SectionHeader number="21" title="VLA guideline under which assistance is being sought" />
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-5 text-sm text-gray-600">
          Help: please see the VLA Handbook at <a href="http://handbook.vla.vic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#0071BC] underline">handbook.vla.vic.gov.au</a>
        </div>
        <div className="space-y-1.5 mb-5">
          <Label htmlFor="vla_guideline_code" className="text-sm font-medium text-gray-700">Select the VLA guideline:</Label>
          <select
            id="vla_guideline_code"
            value={data.vla_guideline_code || ""}
            onChange={(e) => handleGuidelineChange(e.target.value)}
            className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#0071BC] focus:ring-2 focus:ring-[#0071BC]/20"
          >
            <option value="">Select a guideline...</option>
            {GUIDELINE_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((code) => (
                  <option key={code} value={code}>{GUIDELINES[code].label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        {guideline && (
          <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-5">
            <div>
              <p className="text-sm font-medium text-gray-700">Guideline certification</p>
              <p className="text-xs text-gray-500 mt-0.5">Tick the criteria this application meets. These go to the certifier with the application.</p>
            </div>
            {/* Documentary requirements are kept on file rather than certified here, as on
                the Guideline Certification form. */}
            {guideline.criteria.filter((group) => !group.label.toLowerCase().startsWith("documentary")).map((group) => (
              <div key={group.id}>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{group.label}</p>
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <label key={item.id} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!criteria[item.id]}
                        onChange={(e) => setCriterion(item.id, e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#0071BC] shrink-0"
                      />
                      <span className="text-sm text-gray-700 leading-snug">{item.text}</span>
                    </label>
                  ))}
                  {group.id.endsWith("_means") && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!data.guideline_waiver_applies}
                        onChange={(e) => onChange("guideline_waiver_applies", e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#0071BC] shrink-0"
                      />
                      <span className="text-sm text-gray-700 leading-snug">Means test waiver applies</span>
                    </label>
                  )}
                </div>
              </div>
            ))}
            <TextAreaField label="Certification notes (optional)" name="guideline_notes" value={data.guideline_notes} onChange={onChange} rows={3} placeholder="Any compliance notes or merit assessment details for the certifier..." />
          </div>
        )}
        <TextAreaField label="Please identify and address the VLA guideline:" name="vla_guideline" value={data.vla_guideline} onChange={onChange} rows={5} />
      </div>

      <div>
        <SectionHeader number="22" title="Merits of the application" />
        <p className="text-sm text-gray-500 italic mb-3">This question does not need to be completed in indictable matters.</p>
        <TextAreaField label="Please set out the merits of the application:" name="application_merits" value={data.application_merits} onChange={onChange} rows={5} />
      </div>

      <div>
        <SectionHeader number="23" title="Detriment to the applicant if this application is refused" />
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-5 text-sm text-gray-600">
          Help: please see the VLA Handbook at <a href="http://handbook.vla.vic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#0071BC] underline">handbook.vla.vic.gov.au</a>
        </div>
        <TextAreaField label="Please outline the detriment:" name="applicant_detriment" value={data.applicant_detriment} onChange={onChange} rows={5} />
      </div>
    </div>
  );
}